'use client'

import { Chat, useChat } from '@ai-sdk/react'
import { type FileUIPart, type UIMessage, generateId, getToolName, isToolUIPart } from 'ai'
import { isArray, isPlainObject, isString, omit } from 'lodash'
import { useTranslations } from 'next-intl'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useStore } from 'react-redux'

import {
  type ChatTurnRequest,
  type PlaybookId,
  useCreateAttachmentMutation,
  useCreateConversationMutation,
  useLazyGetConversationQuery
} from '~/data/api/client/assistant'
import { invalidateConversationList, toUIMessages } from '~/data/api/client/assistant/enhancedEndpoints'
import { logger } from '~/debug/logger'
import { AssistantAttachmentUploadError, type UploadAttachmentParams } from '~/features/assistant/api'
import type { AssistantAttachment, MessageAttachment } from '~/features/assistant/attachments'
import type { AssistantToolCall } from '~/features/assistant/streamTypes'
import useAssistantAttachments from '~/features/assistant/useAssistantAttachments'
import {
  type ActionProposal,
  attachmentUrl,
  readActionProposal,
  readAttachment,
  readBillingDraft,
  readClarification
} from '~/features/assistant-v2/stream/dataParts'
import mergeStoredMessages from '~/features/assistant-v2/stream/mergeStoredMessages'
import { isHiddenTool } from '~/features/assistant-v2/stream/toolCalls'
import { classifyChatError, createAssistantTransport } from '~/features/assistant-v2/stream/transport'
import { selectViewedEntities } from '~/features/viewedEntities'
import type { RootState } from '~/store'

import { buildComposerContextRefs } from './contextRefs'
import { readSessionConversationId, writeSessionConversationId } from './pendingDraft'
import type { BillingDraft } from './types'
import type { BillingComposerState, ComposerMessage, UseBillingComposerInput } from './useBillingComposer'
import useSessionScope from './useSessionScope'

/** The assistant-api playbook every turn of a composer conversation starts with loaded. */
export const BILLING_COMPOSER_PLAYBOOK: PlaybookId = 'builtin:billing-composer'

/** Text updates arrive per token; batching them keeps long answers from re-rendering the transcript per chunk. */
const RENDER_THROTTLE_MS = 50

type SendOptions = {
  /** Attach the current form as a `billing_draft` context ref (default true when a form exists). */
  includeForm?: boolean
  /** Friendly text for the user bubble when it differs from the message sent to the model. */
  displayText?: string
}

/**
 * What the composer stores on a user message besides its text: the bubble text when it differs from what the model
 * read, and the attachments with the sizes their chips show, which the file parts do not carry.
 */
type ComposerMessageMetadata = {
  displayText?: string
  attachments?: AssistantAttachment[]
}

/**
 * {@link useBillingComposer} on assistant-api: the conversation is created with the billing composer playbook, each
 * turn streams through the assistant-api UI message transport with the page and form context refs, and the
 * transcript, drafts, tax guidance and completeness are read from the streamed message parts. It returns the same
 * shape, so `BillingComposerPanel` renders either.
 */
export function useBillingComposerV2(input: UseBillingComposerInput) {
  const { organizationId, entity, mode, entityId, entityName, pathname, pageTitle, getFormSnapshot } = input
  const t = useTranslations('features.AssistantV2')
  const dispatch = useDispatch()
  // Read when a turn is sent, so the turn carries the entities the page shows at that moment.
  const store = useStore<RootState>()
  const sessionScope = useSessionScope()
  const [createConversation] = useCreateConversationMutation()
  const [createAttachment] = useCreateAttachmentMutation()
  const [fetchConversation] = useLazyGetConversationQuery()

  // Before the first message the chat has no conversation and never sends; `bindChat` swaps in one per conversation.
  const [chat, setChat] = useState(() => new Chat<UIMessage>({}))
  // The chat a send goes to, current before the re-render that hands it to `useChat`.
  const chatRef = useRef(chat)
  // Thumbnails of images picked in this tab, which the stored transcript cannot carry.
  const previewUrlsRef = useRef(new Map<string, string>())
  const conversationIdRef = useRef<string | null>(null)
  const pendingCreateRef = useRef<Promise<string> | null>(null)
  const resumeAttemptedRef = useRef(false)
  // The resume in flight; a send waits for it so the two never bind different conversations.
  const resumeRef = useRef<Promise<void> | null>(null)
  // Bumped by a new chat and an org switch: a send or resume that started under an older epoch is dropped.
  const epochRef = useRef(0)
  // Bumped by Stop: a send still preparing under an older count keeps its line in the transcript but is not sent.
  const stopCountRef = useRef(0)
  const [hasUnreadOutcome, setHasUnreadOutcome] = useState(false)
  const [preparing, setPreparing] = useState(false)
  const [createFailed, setCreateFailed] = useState(false)
  const [resuming, setResuming] = useState(false)
  const [clarificationAnswers, setClarificationAnswers] = useState<Record<string, Record<string, string>>>({})
  const { messages, status, error, stop } = useChat({ chat, experimental_throttle: RENDER_THROTTLE_MS })

  const bindChat = useCallback(
    (conversationId: string, orgId: string, initialMessages: UIMessage[]) => {
      const next = new Chat<UIMessage>({
        id: conversationId,
        messages: initialMessages,
        transport: createAssistantTransport({
          conversationId,
          organizationId: orgId,
          getViewedEntities: () => selectViewedEntities(store.getState())
        }),
        // A turn moves the conversation to the top of the history and may give it a title.
        onFinish: () => {
          dispatch(invalidateConversationList())
        }
      })
      conversationIdRef.current = conversationId
      chatRef.current = next
      setChat(next)
      return next
    },
    [dispatch, store]
  )

  const resetChat = useCallback(() => {
    epochRef.current += 1
    void chatRef.current.stop()
    conversationIdRef.current = null
    pendingCreateRef.current = null
    chatRef.current = new Chat<UIMessage>({})
    setChat(chatRef.current)
    setClarificationAnswers({})
    setCreateFailed(false)
  }, [])

  // Concurrent callers (an upload and the first message) share one create request.
  const ensureConversation = useCallback(
    async (orgId: string): Promise<string> => {
      // An upload picked while the session resumes belongs to the resumed conversation.
      await resumeRef.current
      if (conversationIdRef.current) return conversationIdRef.current
      if (!pendingCreateRef.current) {
        const created = createConversation({ conversationCreateParams: { playbook: BILLING_COMPOSER_PLAYBOOK } })
          .unwrap()
          .then((conversation) => {
            // A new chat or an org switch while the create was in flight discards it.
            if (pendingCreateRef.current !== created) throw new Error('Billing composer conversation was discarded')
            pendingCreateRef.current = null
            // Lines of earlier attempts whose create failed stay in the transcript, as their error does.
            bindChat(conversation.id, orgId, chatRef.current.messages)
            writeSessionConversationId(pathname, conversation.id, sessionScope)
            return conversation.id
          })
          .catch((createError: unknown) => {
            if (pendingCreateRef.current === created) pendingCreateRef.current = null
            throw createError
          })
        pendingCreateRef.current = created
      }
      return pendingCreateRef.current
    },
    [bindChat, createConversation, pathname, sessionScope]
  )

  // assistant-api registers the upload and answers with a presigned URL the browser PUTs the bytes to.
  const uploadAttachment = useCallback(
    async ({ conversationId, file, signal, onProgress }: UploadAttachmentParams): Promise<AssistantAttachment> => {
      const created = await createAttachment({
        conversationId,
        attachmentCreateParams: { filename: file.name, contentType: file.type || null, sizeBytes: file.size }
      }).unwrap()
      const response = await fetch(created.uploadUrl, {
        method: 'PUT',
        headers: created.uploadHeaders,
        body: file,
        signal
      })
      if (!response.ok) throw new AssistantAttachmentUploadError('uploadFailed', response.status)
      onProgress?.(100)
      return {
        attachmentId: created.attachmentId,
        filename: created.filename,
        contentType: created.contentType,
        sizeBytes: created.sizeBytes,
        createdAt: created.createdAt
      }
    },
    [createAttachment]
  )

  const attachments = useAssistantAttachments({ organizationId, ensureConversation, uploadAttachment })
  const { waitForUploads, takeReady, clear: clearAttachments } = attachments

  const send = useCallback(
    async (text: string, options?: SendOptions) => {
      const trimmed = text.trim()
      if (!trimmed || !organizationId) return

      const epoch = epochRef.current
      const stopCount = stopCountRef.current
      setPreparing(true)
      setCreateFailed(false)
      try {
        await resumeRef.current
        // Uploads that are still in flight finish first so their files ride along on this turn.
        await waitForUploads()
        const sentAttachments = takeReady()
        sentAttachments.forEach((attachment) => {
          if (attachment.previewUrl) previewUrlsRef.current.set(attachment.attachmentId, attachment.previewUrl)
        })
        const displayText = options?.displayText?.trim()
        const metadata: ComposerMessageMetadata = {
          ...(displayText && displayText !== trimmed && { displayText }),
          ...(sentAttachments.length > 0 && { attachments: sentAttachments.map(withoutPreview) })
        }
        const files: FileUIPart[] = sentAttachments.map((attachment) => ({
          type: 'file',
          mediaType: attachment.contentType,
          filename: attachment.filename,
          url: attachmentUrl(attachment.attachmentId)
        }))
        const message: UIMessage = {
          id: generateId(),
          role: 'user',
          parts: [...files, { type: 'text', text: trimmed }],
          ...(Object.keys(metadata).length > 0 && { metadata })
        }

        try {
          await ensureConversation(organizationId)
        } catch (createError) {
          logger.error('Billing composer: could not create the conversation', createError)
          if (epoch !== epochRef.current) return
          // The panel has already cleared its input, so the attempt stays in the transcript beside the error.
          chatRef.current.messages = [...chatRef.current.messages, message]
          setCreateFailed(true)
          return
        }
        if (epoch !== epochRef.current) return
        if (stopCount !== stopCountRef.current) {
          // The panel has already cleared its input, so a stopped message stays in the transcript unsent.
          chatRef.current.messages = [...chatRef.current.messages, message]
          return
        }

        const includeForm = options?.includeForm ?? true
        const contextRefs = buildComposerContextRefs({
          entity,
          mode,
          entityId,
          entityName,
          formSnapshot: includeForm ? getFormSnapshot?.() : undefined,
          pathname,
          pageTitle
        })
        const body = {
          contextRefs,
          pageContext: { route: pathname, pageTitle, entities: selectViewedEntities(store.getState()) },
          autoApprove: false
        } satisfies Pick<ChatTurnRequest, 'contextRefs' | 'pageContext' | 'autoApprove'>

        setPreparing(false)
        await chatRef.current.sendMessage(message, { body })
      } finally {
        setPreparing(false)
      }
    },
    [
      ensureConversation,
      entity,
      entityId,
      entityName,
      getFormSnapshot,
      mode,
      organizationId,
      pageTitle,
      pathname,
      store,
      takeReady,
      waitForUploads
    ]
  )

  const answerClarification = useCallback(
    (lineId: string, answerText: string, displayText: string, answers: Record<string, string>) => {
      setClarificationAnswers((prev) => ({ ...prev, [lineId]: answers }))
      return send(answerText, { displayText })
    },
    [send]
  )

  // Stop also cancels a send still waiting for its uploads or its conversation.
  const abort = useCallback(() => {
    stopCountRef.current += 1
    void stop()
  }, [stop])

  // Pending files belong to the conversation they were uploaded to, so a new chat drops them.
  const newChat = useCallback(() => {
    resetChat()
    clearAttachments()
    writeSessionConversationId(pathname, null, sessionScope)
  }, [clearAttachments, pathname, resetChat, sessionScope])

  /**
   * Restores the composer conversation this page started earlier in the session (if any) from its stored
   * transcript, and points the next turn at it. Any other conversation under the session key is forgotten.
   */
  const resumeFromSession = useCallback(async () => {
    if (resumeAttemptedRef.current || !organizationId) return
    resumeAttemptedRef.current = true
    const conversationId = readSessionConversationId(pathname, sessionScope)
    if (!conversationId) return

    const epoch = epochRef.current
    // A new chat or an org switch while the read was in flight owns the panel now.
    const superseded = () => epoch !== epochRef.current || conversationIdRef.current !== null
    setResuming(true)
    const resume = (async () => {
      try {
        const detail = await fetchConversation({ conversationId }).unwrap()
        if (superseded()) return
        if (detail.readOnly || detail.conversation.playbook !== BILLING_COMPOSER_PLAYBOOK) {
          writeSessionConversationId(pathname, null, sessionScope)
          return
        }
        bindChat(conversationId, organizationId, toUIMessages(detail.messages))
      } catch (resumeError) {
        logger.warn('Billing composer: could not resume conversation', resumeError)
        if (!superseded()) writeSessionConversationId(pathname, null, sessionScope)
      } finally {
        setResuming(false)
      }
    })()
    resumeRef.current = resume
    await resume
    if (resumeRef.current === resume) resumeRef.current = null
  }, [bindChat, fetchConversation, organizationId, pathname, sessionScope])

  // A different org means a different conversation: drop the transcript along with the chat.
  const lastOrgRef = useRef(organizationId)
  useEffect(() => {
    if (lastOrgRef.current !== organizationId) {
      lastOrgRef.current = organizationId
      resumeAttemptedRef.current = false
      resetChat()
    }
  }, [organizationId, resetChat])

  // Closing the panel aborts the turn in flight, as the v1 composer's stream is, and drops a send still preparing.
  useEffect(
    () => () => {
      epochRef.current += 1
      void chatRef.current.stop()
    },
    []
  )

  const streaming = status === 'submitted' || status === 'streaming'

  // assistant-api posts a message when a long-running action finishes. A reply in flight is written into the last
  // message, so the stored transcript is read back and merged only once no turn is streaming.
  const onActionSettled = useCallback(() => setHasUnreadOutcome(true), [])
  useEffect(() => {
    const conversationId = conversationIdRef.current
    if (!hasUnreadOutcome || streaming || preparing || !conversationId) return
    setHasUnreadOutcome(false)
    const target = chatRef.current
    fetchConversation({ conversationId })
      .unwrap()
      .then((detail) => {
        if (chatRef.current !== target) return
        // A turn sent while the read was in flight defers the merge until that turn ends.
        if (target.status === 'submitted' || target.status === 'streaming') {
          setHasUnreadOutcome(true)
          return
        }
        target.messages = mergeStoredMessages(target.messages, toUIMessages(detail.messages))
      })
      .catch((loadError: unknown) => logger.error('Billing composer: could not load an action outcome', loadError))
  }, [fetchConversation, hasUnreadOutcome, preparing, streaming])
  const transcript = useMemo(
    () => toComposerTranscript(messages, clarificationAnswers, streaming, previewUrlsRef.current),
    [clarificationAnswers, messages, streaming]
  )

  const errorMessage = createFailed ? t('createError') : error ? chatErrorMessage(error, t) : null
  const state: BillingComposerState = {
    ...transcript,
    busy: preparing || streaming,
    status: streaming ? runningToolStatus(messages.at(-1)) : null,
    error: errorMessage,
    resuming
  }

  return { ...state, send, answerClarification, abort, newChat, resumeFromSession, attachments, onActionSettled }
}

type ComposerTranscript = Pick<
  BillingComposerState,
  'messages' | 'drafts' | 'taxGuidance' | 'completeness' | 'awaitingAnswers'
>

/**
 * The composer's view of an assistant-api transcript: one line per message, every billing draft in arrival order
 * (a re-sent draft id replaces its earlier copy), and the newest tax guidance and completeness.
 */
export function toComposerTranscript(
  messages: UIMessage[],
  clarificationAnswers: Record<string, Record<string, string>>,
  streaming: boolean,
  previewUrls: ReadonlyMap<string, string> = new Map()
): ComposerTranscript {
  const drafts = new Map<string, BillingDraft>()
  const lines = messages.flatMap((message, index): ComposerMessage[] => {
    const isLast = index === messages.length - 1
    if (message.role === 'user') return [toUserLine(message, previewUrls)]
    if (message.role !== 'assistant') return []

    const draftIds: string[] = []
    const proposals: ActionProposal[] = []
    let clarification: ComposerMessage['clarification']
    for (const part of message.parts) {
      const draft = readBillingDraft(part)
      if (draft) {
        drafts.delete(draft.draftId)
        drafts.set(draft.draftId, draft)
        if (!draftIds.includes(draft.draftId)) draftIds.push(draft.draftId)
        continue
      }
      const proposal = readActionProposal(part)
      if (proposal) {
        proposals.push(proposal)
        continue
      }
      clarification = readClarification(part) ?? clarification
    }
    const answers = clarificationAnswers[message.id]
    return [
      {
        id: message.id,
        role: 'assistant',
        text: joinText(message, 'text'),
        thinking: joinText(message, 'reasoning') || undefined,
        toolCalls: toolCallsOf(message),
        streaming: streaming && isLast,
        ...(draftIds.length > 0 && { draftIds }),
        ...(proposals.length > 0 && { proposals }),
        ...(clarification && {
          clarification,
          clarificationAnswers: answers,
          // A clarification is retired once the conversation moves past it without its form being answered.
          clarificationRetired: !answers && !isLast
        })
      }
    ]
  })

  const ordered = [...drafts.values()]
  const lastLine = lines.at(-1)
  return {
    messages: lines,
    drafts: ordered,
    taxGuidance: ordered.findLast((draft) => draft.taxGuidance)?.taxGuidance ?? null,
    completeness: ordered.findLast((draft) => draft.completeness)?.completeness ?? null,
    awaitingAnswers:
      !streaming && Boolean(lastLine?.clarification) && !lastLine?.clarificationAnswers && !lastLine?.clarificationRetired
  }
}

function toUserLine(message: UIMessage, previewUrls: ReadonlyMap<string, string>): ComposerMessage {
  const metadata = readComposerMetadata(message)
  const fileAttachments = message.parts.flatMap((part): MessageAttachment[] => {
    const attachment = readAttachment(part)
    if (!attachment) return []
    const stored = metadata.attachments?.find((item) => item.attachmentId === attachment.attachmentId)
    const previewUrl = previewUrls.get(attachment.attachmentId)
    return [
      {
        ...(stored ?? {
          attachmentId: attachment.attachmentId,
          filename: attachment.filename,
          contentType: attachment.mediaType,
          sizeBytes: 0
        }),
        ...(previewUrl && { previewUrl })
      }
    ]
  })
  return {
    id: message.id,
    role: 'user',
    text: metadata.displayText ?? joinText(message, 'text'),
    ...(fileAttachments.length > 0 && { attachments: fileAttachments })
  }
}

function readComposerMetadata(message: UIMessage): ComposerMessageMetadata {
  if (!isPlainObject(message.metadata)) return {}
  const { displayText, attachments } = message.metadata as Record<string, unknown>
  return {
    ...(isString(displayText) && { displayText }),
    ...(isArray(attachments) && { attachments: attachments.filter(isStoredAttachment) })
  }
}

function isStoredAttachment(value: unknown): value is AssistantAttachment {
  if (!isPlainObject(value)) return false
  const { attachmentId, filename, contentType, sizeBytes } = value as Record<string, unknown>
  return isString(attachmentId) && isString(filename) && isString(contentType) && typeof sizeBytes === 'number'
}

function joinText(message: UIMessage, type: 'text' | 'reasoning'): string {
  return message.parts
    .flatMap((part) => (part.type === type && part.text ? [part.text] : []))
    .join('\n\n')
}

function toolCallsOf(message: UIMessage): AssistantToolCall[] | undefined {
  const calls = message.parts.flatMap((part): AssistantToolCall[] => {
    if (!isToolUIPart(part)) return []
    const name = getToolName(part)
    if (isHiddenTool(name)) return []
    const settled = part.state === 'output-available' || part.state === 'output-error'
    return [{ id: part.toolCallId, name, status: settled ? 'done' : 'running' }]
  })
  return calls.length > 0 ? calls : undefined
}

/** The status line under a turn in flight: the tool it is running, as words. */
function runningToolStatus(message: UIMessage | undefined): string | null {
  if (message?.role !== 'assistant') return null
  const running = toolCallsOf(message)?.findLast((call) => call.status === 'running')
  return running ? running.name.replace(/_/g, ' ') : null
}

type ChatErrorKey = 'quotaExceeded' | 'creditExhausted' | 'accessDenied' | 'turnInProgress' | 'errorGeneric'

function chatErrorMessage(error: Error, t: (key: ChatErrorKey) => string): string {
  switch (classifyChatError(error)) {
    case 'quota':
      return t('quotaExceeded')
    case 'credit':
      return t('creditExhausted')
    case 'access':
      return t('accessDenied')
    case 'busy':
      return t('turnInProgress')
    default:
      return t('errorGeneric')
  }
}

function withoutPreview(attachment: MessageAttachment): AssistantAttachment {
  return omit(attachment, 'previewUrl')
}
